/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { API } from 'aws-amplify';

import { createUser, deleteUsers } from '../../graphql/mutations';
import { isEmpty } from 'lodash';
import {
  getOrgRoleAndPermissions,
  getAllUsers,
  getUserManagers,
  getUserNames,
  getUserPermissions,
  getUserDetails,
} from '../../graphql/queries';
import { BooleanType, CreateUser, DeleteUserInput, UserDetailsInput } from '../../API';
import { AuthHeader, AWSAuthMode, NUMBER_OF_ITEMS_PER_PAGE_IN_FACILITY_TYPES } from '../Constants/App';
import { IGraphQLResponse } from '../Models';
import { getWebInfo } from '../Utilities/utils';
import { AuthenticationService } from './AuthenticationService';

export class OrganizationService {
  static async getOrganizationRoles(orgId: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const usersResponse: IGraphQLResponse = await API.graphql(
        {
          query: getOrgRoleAndPermissions,
          variables: { orgId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: usersResponse?.data?.getOrgRoleAndPermissions ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }
  static async getAllUsers(
    organizationId: string,
    userId?: string,
    roleName?: string,
    pageNumber?: number,
    limit?: number,
    isAssociate?: BooleanType | '',
    isPaginationRequire = false,
    sortBy?: string,
    sortOrder?: string,
    searchString?: string,
    intId?: string
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const variables: {
        isAssociate?: BooleanType;
        limit?: number;
        organizationId: string;
        pageNumber?: number;
        roleName?: string;
        sortBy?: string;
        sortOrder?: string;
        searchString?: string;
        userId?: string;
        intId?: string;
      } = {
        organizationId: organizationId || '',
        limit: limit || 10,
        pageNumber: pageNumber || 1,
      };

      if (roleName) {
        variables.roleName = roleName;
      }
      if (userId) {
        variables.userId = userId;
      }
      if (isAssociate) {
        variables.isAssociate = isAssociate;
      }
      if (sortBy) {
        variables.sortBy = sortBy;
      }
      if (sortOrder) {
        variables.sortOrder = sortOrder;
      }
      if (searchString) {
        variables.searchString = searchString;
      }
      if (intId) {
        variables.intId = intId;
      }
      let usersResponse: IGraphQLResponse = await API.graphql(
        {
          query: getAllUsers,
          variables: { ...variables },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      if (!isPaginationRequire) {
        usersResponse = await API.graphql(
          {
            query: getAllUsers,
            variables: {
              ...variables,
              limit:
                usersResponse?.data?.getAllUsers?.metadata?.totalCount ?? NUMBER_OF_ITEMS_PER_PAGE_IN_FACILITY_TYPES,
            },
            authMode: AWSAuthMode.COGNITO,
          },
          { ...getWebInfo(), ...authHeaders }
        );
      }
      return {
        data: usersResponse?.data?.getAllUsers?.data ?? [],
        metadata: usersResponse?.data?.getAllUsers?.metadata ?? {},
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e?.errors ?? [], metadata: {} };
    }
  }

  static async createUser(userInput: CreateUser) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const createUserResponse: IGraphQLResponse = await API.graphql(
        {
          query: createUser,
          variables: { userInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: createUserResponse?.data?.createUser ?? {}, errors: [] };
    } catch (e: any) {
      return { data: {}, errors: e.errors };
    }
  }

  static async getUserManagers(
    managerId: string,
    userId?: string,
    isAssociate?: BooleanType | '',
    searchString?: string,
    pageNumber: number = 0,
    limit: number = 0,
    sortBy?: string,
    sortOrder?: string,
    isPaginationRequired = false
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const variables: {
        isAssociate?: string;
        limit?: number;
        managerId: string;
        pageNumber?: number;
        searchString?: string;
        userId?: string;
        sortBy?: string;
        sortOrder?: string;
      } = {
        managerId,
      };
      if (!isEmpty(userId)) {
        variables.userId = userId;
      }
      if (!isEmpty(isAssociate)) {
        variables.isAssociate = isAssociate;
      }
      if (!isEmpty(searchString)) {
        variables.searchString = searchString;
      }
      if (pageNumber > 0) {
        variables.pageNumber = pageNumber;
      }
      if (limit > 0) {
        variables.limit = limit;
      }
      if (sortBy) {
        variables.sortBy = sortBy;
      }
      if (sortOrder) {
        variables.sortOrder = sortOrder;
      }
      if (managerId) {
        let userManagersResponse: IGraphQLResponse = await API.graphql(
          {
            query: getUserManagers,
            variables,
            authMode: AWSAuthMode.COGNITO,
          },
          { ...getWebInfo(), ...authHeaders }
        );
        if (!isPaginationRequired) {
          userManagersResponse = await API.graphql(
            {
              query: getUserManagers,
              variables: {
                ...variables,
                limit: userManagersResponse?.data?.getUserManagers?.metadata?.totalCount ?? 20,
              },
              authMode: AWSAuthMode.COGNITO,
            },
            { ...getWebInfo(), ...authHeaders }
          );
        }
        return {
          data: userManagersResponse?.data?.getUserManagers?.data ?? [],
          metadata: userManagersResponse?.data?.getUserManagers?.metadata,
          errors: [],
        };
      }
      return { data: [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async deleteUsers(deleteUserInput: DeleteUserInput) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const deleteUserResponse: IGraphQLResponse = await API.graphql(
        {
          query: deleteUsers,
          variables: { deleteUserInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: deleteUserResponse?.data?.deleteUsers ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getUserNames(getUserNamesInput: { jobId: string; vendorIds?: string; userIds?: string }) {
    const { jobId = '', vendorIds = '', userIds = '' } = getUserNamesInput;

    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const getUserNamesResponse: IGraphQLResponse = await API.graphql(
        {
          query: getUserNames,
          variables: { jobId, vendorIds, userIds },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: getUserNamesResponse?.data?.getUserNames ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getUserRoleAndPermission(userId: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();

      const getUserPermissionsResponse: IGraphQLResponse = await API.graphql(
        {
          query: getUserPermissions,
          variables: { userId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: getUserPermissionsResponse?.data?.getUserPermissions ?? [], error: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getUsersDetails(userDetailsInput: UserDetailsInput) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();

      const userDetailsResponse: IGraphQLResponse = await API.graphql(
        {
          query: getUserDetails,
          variables: { userDetailsInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      return {
        data: userDetailsResponse?.data?.getUserDetails ?? [],
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e?.errors ?? [], metadata: {} };
    }
  }
}
